// resources/js/src/components/community/comments/PostComments.tsx
import React, { useEffect, useState, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import CommentItem from "./CommentItem";
import CommentInput from "./CommentInput";
import { useCommentStore } from "../../../../store/commentStore";
import { useAuthStore } from "../../../../auth/auth.store";
import { ReactionType } from "../post/interfaces/types";
import { ApiComment } from "../../../../services/comment.api";

// ✅ ثابتة بره الكومبوننت — reference واحد لكل render
const EMPTY_COMMENTS: ApiComment[] = [];

type Props = {
  postId: number | string;
};

const PostComments: React.FC<Props> = ({ postId }) => {
  const [text, setText] = useState("");
  const k = String(postId);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // ============ Selectors (primitive) ============
  const isLoading = useCommentStore((s) => s.isLoadingByPost[k] ?? false);
  const isSubmitting = useCommentStore(
    (s) => s.isSubmittingByPost[k] ?? false
  );
  const error = useCommentStore((s) => s.errorByPost[k] ?? "");

  // ============ Selector (array) مع useShallow ============
  const comments = useCommentStore(
    useShallow((s) => s.byPost[k] ?? EMPTY_COMMENTS)
  );

  // ============ Actions (ثابتة) ============
  const loadComments = useCommentStore((s) => s.loadComments);
  const addComment = useCommentStore((s) => s.addComment);
  const editComment = useCommentStore((s) => s.editComment);
  const deleteComment = useCommentStore((s) => s.deleteComment);
  const reactToComment = useCommentStore((s) => s.reactToComment);

  // ============ منع النداء المتكرر لـ loadComments ============
  const loadedRef = useRef<string | null>(null);

  useEffect(() => {
  console.log("[PostComments] useEffect fired, postId:", postId, "k:", k);
  if (loadedRef.current === k) return;
  loadedRef.current = k;
  loadComments(postId);
}, [k, postId, loadComments]);

  // ============ Handlers ============
  const handleTopLevelSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const ok = await addComment(postId, { body: trimmed });
    if (ok) setText("");
  };

  const handleReply = async (parentId: number, body: string) => {
    return addComment(postId, { body, parentId });
  };

  const handleEdit = async (commentId: number, body: string) => {
    return editComment(postId, commentId, body);
  };

  const handleDelete = async (commentId: number) => {
    return deleteComment(postId, commentId);
  };

  const handleReact = async (commentId: number, reaction: ReactionType) => {
    return reactToComment(postId, commentId, reaction);
  };

  // ============ Render: Loading ============
  if (isLoading && comments.length === 0) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  // ============ Render: Main ============
  return (
    <div className="px-4 pt-3">
      {/* Input */}
      <CommentInput
        text={text}
        setText={setText}
        onSubmit={handleTopLevelSubmit}
        submitting={isSubmitting}
      />

      {/* Error */}
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}

      {/* Comments list */}
      <div className="mt-4 space-y-4">
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            postId={postId}
            onReply={handleReply}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReact={handleReact}
          />
        ))}

        {comments.length === 0 && !isLoading && (
          <p className="text-center text-sm text-gray-400 py-4">
            No comments yet. Be the first to comment!
          </p>
        )}
      </div>
    </div>
  );
};

export default PostComments;